/**
 * CR500 Phase 2B: Flip Presentation Mode
 * CR500 Phase 6A: Telemetry integration
 * CR324: Removed hardcoded header/nav - now purely block-driven
 * CR309: Details CTA is now block-driven via details_cta block
 * CR323: Added fallback trigger to prevent stranded cards in flip mode
 * 
 * Renders the deal card with flip-to-back interaction.
 * Details are shown on the back face using v2 blocks.
 * 
 * The back_header and back_nav blocks render the header/nav chrome.
 * They are toggled via surfaceOverrides.details.blockToggles.
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import type { Deal } from "@/lib/types/deals";
import type { ResolvedDealCardSpecV2 } from "@/lib/deal-cards/specV2/types";
import type { DetailsEntryConfig } from "@/lib/layout-studio/detailsPresentationTypes";
import { DEFAULT_DETAILS_ENTRY } from "@/lib/layout-studio/detailsPresentationTypes";
import { DetailsBlocksRenderer } from "./DetailsBlocksRenderer";
import { trackDetailsEvent } from "@/lib/deal-cards/telemetry";
import { DetailsCTABlock, type DetailsCTAConfig, hasEnabledDetailsContent } from "@/lib/deal-cards/blocks/DetailsCTABlock";
import { cn } from "@/lib/utils";

// ================================================================
// TYPES
// ================================================================

export interface FlipPresentationProps {
  /** The deal being displayed */
  deal: Deal & { id?: string };
  /** The resolved v2 spec */
  spec: ResolvedDealCardSpecV2;
  /** Admin context */
  isAdmin?: boolean;
  /** Height class for the flip container */
  heightClass?: string;
  /** Height in pixels (takes precedence over heightClass) */
  heightPx?: number;
  /** Entry button configuration */
  entryConfig?: DetailsEntryConfig;
  /** The front face content */
  children: React.ReactNode;
  /** External control of flipped state */
  isFlipped?: boolean;
  /** External flip handler */
  onFlip?: (flipped: boolean) => void;
}

// ================================================================
// HELPERS
// ================================================================

/**
 * CR323: Check if a flip trigger is available (details_cta or cta_details enabled).
 * Uses both canonical and legacy key names for backwards compatibility.
 */
function hasFlipTrigger(spec: ResolvedDealCardSpecV2): boolean {
  const primaryBlocks = spec.surfaces.primary.blocks;
  
  // Check canonical key: details_cta
  const detailsCta = primaryBlocks.find(b => b.id === "details_cta");
  if (detailsCta?.enabled) return true;
  
  // Check legacy alias: cta_details (backwards compat)
  const ctaDetails = primaryBlocks.find(b => b.id === "cta_details");
  if (ctaDetails?.enabled) return true;
  
  return false;
}

// ================================================================
// COMPONENT
// ================================================================

export function FlipPresentation({
  deal,
  spec,
  isAdmin = false,
  heightClass = "h-[400px]",
  heightPx,
  entryConfig = DEFAULT_DETAILS_ENTRY,
  children,
  isFlipped: externalFlipped,
  onFlip,
}: FlipPresentationProps) {
  // Internal state (used when not controlled externally)
  const [internalFlipped, setInternalFlipped] = useState(false);
  
  // Use external state if provided
  const isFlipped = externalFlipped !== undefined ? externalFlipped : internalFlipped;
  
  const handleFlip = useCallback(() => {
    const newState = !isFlipped;
    // Track details open event
    if (newState) {
      trackDetailsEvent("open", "v2", spec.resolved.dealTypeKey);
    }
    if (onFlip) {
      onFlip(newState);
    } else {
      setInternalFlipped(newState);
    }
  }, [isFlipped, onFlip, spec.resolved.dealTypeKey]);
  
  const handleFlipBack = useCallback(() => {
    // Track flip back event
    trackDetailsEvent("flip_back", "v2", spec.resolved.dealTypeKey);
    if (onFlip) {
      onFlip(false);
    } else {
      setInternalFlipped(false);
    }
  }, [onFlip, spec.resolved.dealTypeKey]);
  
  // ESC key handler
  useEffect(() => {
    if (!isFlipped) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleFlipBack();
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFlipped, handleFlipBack]);
  
  // CR309: Get details_cta block from spec (with fallback config)
  const detailsCtaBlock = useMemo(() => {
    const primaryBlocks = spec.surfaces.primary.blocks;
    
    // Check canonical key first
    const ctaBlock = primaryBlocks.find(b => b.id === "details_cta");
    if (ctaBlock) return ctaBlock;
    
    // Check legacy alias: cta_details
    const legacyBlock = primaryBlocks.find(b => b.id === "cta_details");
    if (legacyBlock) return { ...legacyBlock, id: "details_cta" }; // Normalize to canonical key
    
    // Fallback: create block from entryConfig (legacy compatibility)
    return {
      id: "details_cta",
      type: "details_cta" as const,
      enabled: true,
      order: 12,
      config: {
        enabled: true,
        label: entryConfig.label,
        placement: "responsive" as const,
        breakpoint: "md" as const,
        variant: entryConfig.variant,
        floatingOffset: { right: 12, bottom: 12 },
      } satisfies DetailsCTAConfig,
    };
  }, [spec.surfaces.primary.blocks, entryConfig]);
  
  // CR323: Check if we have a configured flip trigger
  const hasTrigger = hasFlipTrigger(spec);
  
  // CR323: Check if back surface has content worth flipping to
  const hasBackContent = hasEnabledDetailsContent(spec);
  
  // CR323: Debug warning for missing trigger
  const showFallbackTrigger = !hasTrigger && hasBackContent;
  
  useEffect(() => {
    if (showFallbackTrigger && isAdmin) {
      console.warn(
        "[FlipPresentation] Flip mode enabled but no trigger configured — fallback active",
        { dealType: spec.resolved.dealTypeKey, variantKey: spec.resolved.variantKey }
      );
    }
  }, [showFallbackTrigger, isAdmin, spec.resolved.dealTypeKey, spec.resolved.variantKey]);
  
  // Height styling
  const heightStyle = heightPx ? { height: `${heightPx}px` } : undefined;
  const effectiveHeightClass = heightPx ? "" : heightClass;
  
  return (
    <div 
      className={cn(
        "relative w-full perspective-1000",
        effectiveHeightClass,
      )}
      style={{ perspective: "1000px", ...heightStyle }}
    >
      <div
        className={cn(
          "relative w-full h-full transition-transform duration-500 transform-style-preserve-3d",
          isFlipped && "rotate-y-180"
        )}
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front Face - The Card */}
        <div
          className="absolute inset-0 w-full h-full backface-hidden"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="relative h-full">
            {children}
            
            {/* CR309/CR310: Block-driven Details CTA - pill in bottom-right */}
            {hasTrigger && (
              <DetailsCTABlock
                deal={deal}
                block={detailsCtaBlock}
                context={{
                  isAdmin,
                  surface: "primary",
                  labels: spec.resolved.labels,
                  styleOverrides: spec.resolved.styleOverrides,
                }}
                onClick={handleFlip}
                renderMode="floating-only"
                spec={spec}
              />
            )}
            
            {/* CR323: Fallback trigger when no configured trigger exists */}
            {showFallbackTrigger && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFlip();
                }}
                className={cn(
                  "absolute z-20 px-2.5 py-1 text-xs font-medium rounded-full",
                  "bg-background/90 text-foreground/80 hover:text-foreground",
                  "border border-border/50 hover:border-border",
                  "backdrop-blur-sm shadow-sm hover:shadow transition-all duration-200",
                  "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary/30"
                )}
                style={{ right: 12, bottom: 12 }}
                aria-label="Show deal details"
                data-block="details_cta_fallback"
                data-fallback="true"
              >
                Details ›
              </button>
            )}
          </div>
        </div>
        
        {/* Back Face - Details (purely block-driven per CR324) */}
        <div
          className={cn(
            "absolute inset-0 w-full h-full backface-hidden bg-card border border-border rounded-lg shadow-md overflow-hidden flex flex-col",
            "rotate-y-180"
          )}
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {/* All chrome (header, nav) rendered via blocks */}
          <DetailsBlocksRenderer
            deal={deal}
            spec={spec}
            isAdmin={isAdmin}
            mode="flip"
            onFlipBack={handleFlipBack}
          />
        </div>
      </div>
    </div>
  );
}

export default FlipPresentation;
